(() => {
  const IMAGE_API_BASE = "https://innovative-stack-i1z8l.ampt.app/api/v1";
  const CREDIT_API_BASE =
    window.SHERPA_CREDIT_API_BASE ||
    "https://innovative-stack-i1z8l.ampt.app/api/v1";
  const API_BASE = IMAGE_API_BASE;

  const uploadRefs = {
    before: document.querySelector("#product__upload--image__result--modal .product__upload--image__result--before"),
    after: document.querySelector("#product__upload--image__result--modal .product__upload--image__result--after"),
    modal: document.getElementById("product__upload--image__result--modal"),
    label: document.getElementById("upload--image__label"),
    input: document.getElementById("upload--image__trigger"),
    responseContainer: document.getElementById("response--image"),
    close: document.getElementById("close_modal"),
    cancelButton: document.getElementById("upload_download_cancel"),
    sizeText: document.querySelector(".size__text"),
    customerIdInput: document.getElementById("customer-id"),
    creditsMessage: document.getElementById("credits--message"),
    creditsRemaining: document.getElementById("credits--remaining"),
    creditsCount: document.getElementById("credits--count"),
  };

  const MAX_MB = 20;
  // Ampt gateway rejects oversized rasters; convert/resize before upload.
  const MAX_AMPT_UPLOAD_BYTES = 4 * 1024 * 1024;
  const MAX_AMPT_UPLOAD_EDGE = 2048;
  const ACCEPTED_INPUT = /\.(png|jpe?g|webp|pdf)$/i;
  const isLogged = typeof window.userIsLoggedIn !== "undefined" ? !!window.userIsLoggedIn : false;
  const customerId = uploadRefs.customerIdInput?.value || null;
  const loadingOverlay = document.querySelector(".loading__overlay");
  const loadingCancelBtn = document.getElementById("sherpa-ai-loading-cancel");
  let activeAbortController = null;

  const isPdfFile = (file) => {
    if (!file) return false;
    const type = String(file.type || "").toLowerCase();
    return type === "application/pdf" || /\.pdf$/i.test(file.name || "");
  };

  const isAcceptedInput = (file) => {
    if (!file) return false;
    const type = String(file.type || "").toLowerCase();
    if (
      type === "image/png" ||
      type === "image/jpeg" ||
      type === "image/jpg" ||
      type === "image/webp" ||
      type === "application/pdf"
    ) {
      return true;
    }
    return ACCEPTED_INPUT.test(file.name || "");
  };

  const ensurePdfJs = () => {
    if (!window.pdfjsLib) throw new Error("Lecteur PDF indisponible. Rechargez la page.");
    if (window.SHERPA_PDF_WORKER_URL) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = window.SHERPA_PDF_WORKER_URL;
    }
    return window.pdfjsLib;
  };

  const canvasToBlob = (canvas, type, quality) =>
    new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) reject(new Error("Export canvas impossible"));
          else resolve(blob);
        },
        type,
        quality
      );
    });

  const renderPdfFirstPage = async (file) => {
    const pdfjsLib = ensurePdfJs();
    const buffer = await file.arrayBuffer();
    const doc = await pdfjsLib.getDocument({ data: buffer }).promise;
    if (!doc.numPages) throw new Error("PDF vide");
    const page = await doc.getPage(1);
    const viewportBase = page.getViewport({ scale: 1 });
    const renderScale = Math.min(
      300 / 72,
      MAX_AMPT_UPLOAD_EDGE / Math.max(viewportBase.width, viewportBase.height)
    );
    const viewport = page.getViewport({ scale: renderScale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) throw new Error("Contexte canvas indisponible");
    await page.render({ canvasContext: ctx, viewport, background: "rgba(0,0,0,0)" }).promise;
    return canvas;
  };

  const loadRasterToCanvas = async (file) => {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Image illisible"));
        image.src = url;
      });
      let width = Math.max(1, img.naturalWidth || img.width || 1);
      let height = Math.max(1, img.naturalHeight || img.height || 1);
      const edgeScale = Math.min(1, MAX_AMPT_UPLOAD_EDGE / Math.max(width, height));
      width = Math.max(1, Math.round(width * edgeScale));
      height = Math.max(1, Math.round(height * edgeScale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d", { alpha: true });
      if (!ctx) throw new Error("Contexte canvas indisponible");
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      return canvas;
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const canvasToAmptFile = async (sourceCanvas) => {
    let width = sourceCanvas.width;
    let height = sourceCanvas.height;
    let canvas = sourceCanvas;
    let blob = await canvasToBlob(canvas, "image/png");

    if (blob.size > MAX_AMPT_UPLOAD_BYTES) {
      const shrink = Math.sqrt(MAX_AMPT_UPLOAD_BYTES / Math.max(1, blob.size));
      if (shrink < 0.999) {
        const scaled = document.createElement("canvas");
        scaled.width = Math.max(1, Math.round(width * shrink));
        scaled.height = Math.max(1, Math.round(height * shrink));
        const sctx = scaled.getContext("2d", { alpha: true });
        if (!sctx) throw new Error("Contexte canvas indisponible");
        sctx.clearRect(0, 0, scaled.width, scaled.height);
        sctx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
        canvas = scaled;
        blob = await canvasToBlob(scaled, "image/png");
        if (blob.size > MAX_AMPT_UPLOAD_BYTES) {
          blob = await canvasToBlob(scaled, "image/jpeg", 0.92);
        }
      }
    }

    if (blob.size > MAX_AMPT_UPLOAD_BYTES) {
      throw new Error(
        `Image trop lourde pour l'IA (max ${Math.round(MAX_AMPT_UPLOAD_BYTES / (1024 * 1024))} Mo).`
      );
    }

    const ext = blob.type === "image/jpeg" ? "jpg" : "png";
    return new File([blob], `remove-bg.${ext}`, { type: blob.type || "image/png" });
  };

  /** Ampt accepts PNG/JPEG only — rasterize PDF / normalize WebP first. */
  const prepareAmptUploadFile = async (file) => {
    const canvas = isPdfFile(file) ? await renderPdfFirstPage(file) : await loadRasterToCanvas(file);
    return { uploadFile: await canvasToAmptFile(canvas), previewCanvas: canvas };
  };

  // Escape header / left-nav stacking contexts
  [uploadRefs.modal, loadingOverlay].forEach((el) => {
    if (el && el.parentElement !== document.body) document.body.appendChild(el);
  });

  const goLogin = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const ret = encodeURIComponent(location.pathname + location.search + location.hash);
    window.location.href = "/account/login?return_url=" + ret;
  };

  if (uploadRefs.label) {
    ["dragenter", "dragover", "dragleave", "drop"].forEach((evt) => {
      uploadRefs.label.addEventListener(
        evt,
        (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        { capture: true }
      );
    });
  }

  if (!isLogged) {
    const forceLogin = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      goLogin(e);
    };
    uploadRefs.label?.addEventListener("click", forceLogin, { capture: true });
    uploadRefs.input?.addEventListener("click", forceLogin, { capture: true });
    uploadRefs.input?.addEventListener("change", forceLogin, { capture: true });

    document.querySelectorAll(".product__upload--label").forEach((item, idx) => {
      item.style.display = idx === 0 ? "flex" : "none";
    });

    return;
  }

  let currentCredits = null;
  let downloadInFlight = false;

  const updateCreditsUI = (balance) => {
    currentCredits = balance;

    if (balance <= 0) {
      uploadRefs.creditsMessage?.classList.add("active");
      uploadRefs.label?.classList.add("disabled");
      if (uploadRefs.creditsRemaining) uploadRefs.creditsRemaining.style.display = "none";
    } else {
      uploadRefs.creditsMessage?.classList.remove("active");
      uploadRefs.label?.classList.remove("disabled");
      if (uploadRefs.creditsRemaining) uploadRefs.creditsRemaining.style.display = "block";
      if (uploadRefs.creditsCount) uploadRefs.creditsCount.textContent = balance;
    }
  };

  const fetchCredits = async () => {
    if (!customerId) return;
    try {
      const resp = await fetch(`${CREDIT_API_BASE}/credit/balance?customer_id=${customerId}`);
      if (!resp.ok) return;
      const data = await resp.json();
      updateCreditsUI(data.balance ?? 0);
    } catch (err) {
      console.warn("Credit check failed:", err);
    }
  };

  const useCredit = async () => {
    if (!customerId) return false;
    try {
      const resp = await fetch(`${CREDIT_API_BASE}/credit/use`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customerId }),
      });
      if (!resp.ok) return false;
      const data = await resp.json();
      updateCreditsUI(data.balance ?? (currentCredits - 1));
      return true;
    } catch (err) {
      console.warn("Credit use failed:", err);
      return false;
    }
  };

  fetchCredits();

  if (uploadRefs.input && uploadRefs.label) {
    uploadRefs.input.onchange = function () {
      const file = this.files?.[0];
      if (file && file.size > MAX_MB * 1024 * 1024) {
        uploadRefs.sizeText?.classList.add("active");
        uploadRefs.label.style.outline = "2px solid red";
        this.value = "";
      } else {
        uploadRefs.sizeText?.classList.remove("active");
        uploadRefs.label.style.outline = "2px solid yellow";
      }
    };
  }

  uploadRefs.modal?.addEventListener("click", (e) => {
    if (e.target === e.currentTarget) {
      e.currentTarget.classList.add("hide");
      setTimeout(() => window.location.reload(), 1000);
    }
  });
  uploadRefs.close?.addEventListener("click", () => {
    uploadRefs.modal?.classList.add("hide");
    setTimeout(() => window.location.reload(), 1000);
  });
  uploadRefs.cancelButton?.addEventListener("click", () => {
    uploadRefs.modal?.classList.add("hide");
    setTimeout(() => window.location.reload(), 1000);
  });

  const imageInputHandler = () => {
    if (!uploadRefs.label || !uploadRefs.input) return;

    const fileManager = async (file) => {
      if (!file) return;

      if (currentCredits !== null && currentCredits <= 0) {
        alert("Vous n'avez plus de crédits. Veuillez en acheter.");
        return;
      }

      if (!isAcceptedInput(file)) {
        alert("Formats acceptés : PNG, JPG, JPEG, WebP, PDF.");
        return;
      }

      if (file.size > MAX_MB * 1024 * 1024) {
        uploadRefs.sizeText?.classList.add("active");
        uploadRefs.label.style.outline = "2px solid red";
        alert(`Fichier trop volumineux (max ${MAX_MB} Mo).`);
        return;
      }

      uploadRefs.label.style.outline = "2px solid yellow";

      const loading = loadingOverlay || document.querySelector(".loading__overlay");
      const preloader = loading?.querySelector(".preloader") || document.querySelector(".loading__overlay .preloader");
      let progress = 0;
      let interval;

      const slowProgress = () => {
        if (!preloader) return;
        interval = setInterval(() => {
          if (progress < 99) {
            progress++;
            preloader.textContent = progress + "%";
          }
        }, 500);
      };

      const cleanupLoading = () => {
        if (interval) clearInterval(interval);
        interval = null;
        loading?.classList.remove("active");
        if (preloader) preloader.textContent = "0%";
        progress = 0;
        activeAbortController = null;
        if (uploadRefs.input) uploadRefs.input.value = "";
      };

      const onLoadingCancel = () => {
        activeAbortController?.abort();
        cleanupLoading();
      };
      loadingCancelBtn?.addEventListener("click", onLoadingCancel);

      activeAbortController = new AbortController();
      loading?.classList.add("active");
      slowProgress();

      try {
        const { uploadFile, previewCanvas } = await prepareAmptUploadFile(file);
        if (uploadRefs.before && previewCanvas) {
          uploadRefs.before.innerHTML = "";
          uploadRefs.before.appendChild(previewCanvas);
        }
        const formData = new FormData();
        formData.append("image", uploadFile);

        const resp = await fetch(`${API_BASE}/remove-image-background`, {
          method: "POST", body: formData, signal: activeAbortController.signal
        });
        if (!resp.ok) throw new Error(`AI API ${resp.status}`);

        const blobImage = await resp.blob();
        const resultUrl = URL.createObjectURL(blobImage);

        if (uploadRefs.after) {
          const img = document.createElement("img");
          img.src = resultUrl;
          uploadRefs.after.innerHTML = "";
          uploadRefs.after.appendChild(img);
          uploadRefs.after.classList.add("result_image");
        }

        if (interval) clearInterval(interval);
        const revealResult = () => {
          loadingCancelBtn?.removeEventListener("click", onLoadingCancel);
          cleanupLoading();
          uploadRefs.label.style.outline = "2px solid green";
          uploadRefs.modal?.classList.remove("hide");
        };
        if (preloader && loading) {
          const fast = setInterval(() => {
            if (progress < 100) {
              progress++;
              preloader.textContent = progress + "%";
            } else {
              clearInterval(fast);
              revealResult();
            }
          }, 10);
        } else {
          revealResult();
        }

        setTimeout(() => {
          const btn = document.querySelector(".upload_download");
          if (!btn) return;
          btn.onclick = async (e) => {
            e.preventDefault();
            if (downloadInFlight) return;
            downloadInFlight = true;
            btn.style.pointerEvents = "none";
            btn.style.opacity = "0.6";

            if (interval) clearInterval(interval);
            progress = 0;
            if (preloader) preloader.textContent = "0%";
            loading?.classList.add("active");
            slowProgress();

            const credited = await useCredit();
            if (!credited) {
              downloadInFlight = false;
              btn.style.pointerEvents = "";
              btn.style.opacity = "";
              cleanupLoading();
              alert("Vous n'avez plus de crédits. Veuillez en acheter.");
              return;
            }

            const a = document.createElement("a");
            a.style.display = "none";
            a.href = resultUrl;
            a.download = "printmydtf_sans_fond.png";
            document.body.appendChild(a);
            a.click();

            if (interval) clearInterval(interval);
            if (preloader && loading) {
              const fast2 = setInterval(() => {
                if (progress < 100) {
                  progress++;
                  preloader.textContent = progress + "%";
                } else {
                  clearInterval(fast2);
                  loading.classList.remove("active");
                  preloader.textContent = "0%";
                  progress = 0;
                }
              }, 10);
            }
            setTimeout(() => window.location.reload(), 1000);
          };
        }, 100);

        const addHoverZoom = (sel) => {
          const container = document.querySelector(sel);
          const element = container?.querySelector("img");
          if (!container || !element) return;
          container.addEventListener("mousemove", (e) => {
            const { left, top, width, height } = container.getBoundingClientRect();
            const mouseX = e.clientX - left;
            const mouseY = e.clientY - top;
            const offsetX = (mouseX / width - 0.5) * 3;
            const offsetY = (mouseY / height - 0.5) * 3;
            element.style.transformOrigin = `${50 + offsetX * 25}% ${50 + offsetY * 25}%`;
          });
          container.addEventListener("mouseenter", () => (element.style.transform = "scale(3)"));
          container.addEventListener("mouseleave", () => (element.style.transform = "scale(1)"));
        };
        addHoverZoom(".product__upload--image__result--before");
        addHoverZoom(".product__upload--image__result--after");
      } catch (err) {
        loadingCancelBtn?.removeEventListener("click", onLoadingCancel);
        if (err?.name === "AbortError") {
          cleanupLoading();
          return;
        }
        console.warn("AI API error:", err);
        cleanupLoading();
        uploadRefs.label.style.outline = "2px solid red";
        alert("Le traitement a échoué. Merci de réessayer avec un autre fichier.");
      }
    };

    uploadRefs.input?.addEventListener("change", (e) => {
      const file = e.target.files?.[0];
      fileManager(file);
    });
  };

  imageInputHandler();

  document.querySelectorAll(".product__upload--label").forEach((item, idx) => {
    item.style.display = idx === 0 ? "flex" : "none";
  });
})();